import { useState, useEffect, useCallback } from 'react';
import { delegationsAPI, projectsAPI, usersAPI } from '../utils/api';
import { useAuth } from '../hooks/useAuth';
import { PageHeader } from '../components/layout/UI';
import { useToast, apiError, useConfirm } from '../components/Toast';

// ── Mini avatar ────────────────────────────────────────────────────────────
const Av = ({ name, color }) => (
  <div className="w-8 h-8 rounded-lg flex items-center justify-center font-bold text-white text-xs flex-shrink-0"
    style={{ background: color || '#2E539F' }}>
    {name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
  </div>
);

// ── Formatting helpers ─────────────────────────────────────────────────────
const fmtDate = d => d
  ? new Date(d + 'T00:00:00').toLocaleDateString(undefined, { month:'short', day:'numeric', year:'numeric' })
  : '—';

const getDays = d => Math.max(1, Math.ceil(
  (new Date(d.end_date + 'T00:00:00') - new Date(d.start_date + 'T00:00:00')) / 86400000
) + 1);

// ── Workflow status badge ──────────────────────────────────────────────────
const STATUS_MAP = {
  pending_acceptance: { label:'Awaiting Acceptance', bg:'#FEF3C7', color:'#92400E' },
  accepted:           { label:'Active',              bg:'#D1FAE5', color:'#065f46' },
  declined:           { label:'Declined',            bg:'#FEE2E2', color:'#991B1B' },
  revoked:            { label:'Revoked',             bg:'#F1F5F9', color:'#64748b' },
};

const WorkflowBadge = ({ status }) => {
  const s = STATUS_MAP[status] || STATUS_MAP.revoked;
  return (
    <span className="text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap"
      style={{ background: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
};

const isCurrentlyActive = d =>
  d.workflow_status === 'accepted'
  && new Date(d.start_date + 'T00:00:00') <= new Date()
  && new Date(d.end_date   + 'T00:00:00') >= new Date();

// ── Table primitives ───────────────────────────────────────────────────────
const Th = ({ children }) => (
  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider"
    style={{ color:'var(--text-secondary)', background:'var(--surface-alt)', borderBottom:'1px solid var(--border)' }}>
    {children}
  </th>
);
const Td = ({ children, className = '' }) => (
  <td className={`px-4 py-3 text-sm border-b ${className}`}
    style={{ color:'var(--text)', borderColor:'var(--border)' }}>
    {children}
  </td>
);
const EmptyRow = ({ cols, message }) => (
  <tr>
    <td colSpan={cols} className="text-center py-12 text-sm" style={{ color:'var(--text-secondary)' }}>
      {message}
    </td>
  </tr>
);

// ── Scope display ──────────────────────────────────────────────────────────
const ScopeCell = ({ d }) => {
  if (!d.scoped_projects || d.scoped_projects.length === 0)
    return <span className="text-xs font-medium text-emerald-600">All tasks</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {d.scoped_projects.map(p => (
        <span key={p.id} className="text-xs px-1.5 py-0.5 rounded"
          style={{ background:'var(--surface-alt)', color:'var(--text-secondary)' }}>
          {p.name}
        </span>
      ))}
    </div>
  );
};

// ── Main page ──────────────────────────────────────────────────────────────
export default function DelegationPage() {
  const confirm = useConfirm();
  const toast = useToast();
  const { user } = useAuth();
  const today = new Date().toISOString().split('T')[0];

  const [tab,       setTab]      = useState('mine');
  const [mine,      setMine]     = useState([]);
  const [received,  setReceived] = useState([]);
  const [users,     setUsers]    = useState([]);
  const [projects,  setProjects] = useState([]);
  const [loading,   setLoading]  = useState(true);
  const [showForm,  setShowForm] = useState(false);
  const [saving,    setSaving]   = useState(false);
  const [error,     setError]    = useState('');
  const [scopeMode, setScopeMode]= useState('all');

  const [form, setForm] = useState({
    delegate_id: '', start_date: today, end_date: '', reason: '', project_ids: [],
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dRes, uRes, pRes] = await Promise.all([
        delegationsAPI.getAll(),
        usersAPI.getAll(),
        projectsAPI.getAll({ limit: 0 }),
      ]);
      setMine(dRes.data.my_delegations       || []);
      setReceived(dRes.data.received_delegations || []);
      setUsers(uRes.data.filter(u => u.id !== user?.id && u.is_active));
      setProjects(pRes.data || []);
    } catch(e) { toast.error(apiError(e, 'Something went wrong loading this.')); }
    finally { setLoading(false); }
  }, [user?.id]);

  useEffect(() => { load(); }, [load]);

  const toggleProject = id =>
    setForm(f => ({
      ...f,
      project_ids: f.project_ids.includes(id)
        ? f.project_ids.filter(p => p !== id)
        : [...f.project_ids, id],
    }));

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.delegate_id) { setError('Please select a colleague to delegate to'); return; }
    if (!form.end_date)    { setError('Please set an end date'); return; }
    if (scopeMode === 'projects' && form.project_ids.length === 0) {
      setError('Please select at least one project, or switch scope to "All tasks"'); return;
    }
    setSaving(true);
    try {
      await delegationsAPI.create({
        ...form,
        project_ids: scopeMode === 'projects' ? form.project_ids : null,
      });
      setShowForm(false);
      setForm({ delegate_id:'', start_date:today, end_date:'', reason:'', project_ids:[] });
      setScopeMode('all');
      setTab('mine');
      load();
    } catch(err) {
      setError(err.response?.data?.message || 'Could not create delegation');
    } finally { setSaving(false); }
  };

  const action = async (id, endpoint, confirmMsg) => {
    if (confirmMsg && !await confirm(confirmMsg)) return;
    try { await delegationsAPI[endpoint](id); load(); }
    catch(e) { toast.error(apiError(e, `Could not ${endpoint}`)); }
  };

  const handleDelete = async (id) => {
    if (!await confirm('Delete this delegation record permanently?')) return;
    try { await delegationsAPI.remove(id); load(); }
    catch(e) { toast.error(apiError(e, 'Could not delete')); }
  };

  // Count pending received delegations for the tab badge
  const pendingCount = received.filter(d => d.workflow_status === 'pending_acceptance').length;

  return (
    <div>
      <PageHeader
        title="Delegation"
        subtitle="Delegate your tasks professionally — with accept/decline, project scope, and full audit trail"
        actions={
          <button onClick={() => { setShowForm(v => !v); setError(''); }}
            className="btn-primary text-sm">
            {showForm ? 'Cancel' : '+ New Delegation'}
          </button>
        }
      />

      {/* ── Currently covering banner ─────────────────────────── */}
      {received.filter(isCurrentlyActive).length > 0 && (
        <div className="mb-5 rounded-xl p-4"
          style={{ background:'rgba(46,83,159,.06)', border:'1px solid rgba(46,83,159,.2)', borderLeft:'4px solid #2E539F' }}>
          <p className="font-semibold text-sm mb-2" style={{ color:'#2E539F' }}>
            🛡 You are currently covering for {received.filter(isCurrentlyActive).length === 1 ? 'a colleague' : 'colleagues'}
          </p>
          {received.filter(isCurrentlyActive).map(d => (
            <div key={d.id} className="flex items-start gap-3 mt-2">
              <Av name={d.delegator_name} color={d.delegator_color} />
              <div>
                <p className="text-sm font-medium" style={{ color:'var(--text)' }}>{d.delegator_name}</p>
                <p className="text-xs" style={{ color:'var(--text-secondary)' }}>
                  Until {fmtDate(d.end_date)}
                  {d.scoped_projects?.length > 0
                    ? ` · Projects: ${d.scoped_projects.map(p => p.name).join(', ')}`
                    : ' · All tasks'}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Create delegation form ────────────────────────────── */}
      {showForm && (
        <div className="card mb-5">
          <h2 className="font-semibold text-sm uppercase tracking-wider mb-4"
            style={{ color:'var(--text-secondary)' }}>New Delegation Request</h2>
          <p className="text-xs mb-4 p-3 rounded-lg"
            style={{ background:'rgba(255,200,73,.12)', color:'#854F0B' }}>
            💡 Your colleague will receive a notification and must <strong>accept</strong> before their access is granted.
            You can delegate to multiple people independently by creating separate records.
          </p>

          <form onSubmit={handleCreate}>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div className="md:col-span-2">
                <label className="label">Delegate to <span className="text-red-500">*</span></label>
                <select className="select" value={form.delegate_id}
                  onChange={e => setForm(f => ({ ...f, delegate_id: e.target.value }))}>
                  <option value="">Select a team member…</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.name} — {u.role}</option>)}
                </select>
              </div>
              <div>
                <label className="label">From <span className="text-red-500">*</span></label>
                <input type="date" className="input" value={form.start_date} min={today}
                  onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} />
              </div>
              <div>
                <label className="label">Until <span className="text-red-500">*</span></label>
                <input type="date" className="input" value={form.end_date}
                  min={form.start_date || today}
                  onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} />
              </div>
            </div>

            {/* Scope selector */}
            <div className="mb-4">
              <label className="label">Delegation Scope</label>
              <div className="grid grid-cols-2 gap-3 mt-1">
                {[
                  { val:'all',      label:'All my tasks',      desc:'Full handover — everything assigned to you' },
                  { val:'projects', label:'Specific projects',  desc:'Partial handover — choose which projects' },
                ].map(opt => (
                  <label key={opt.val}
                    className={`cursor-pointer rounded-xl px-4 py-3 border-2 transition-colors ${scopeMode === opt.val ? 'border-brand-500' : 'border-transparent'}`}
                    style={{ background:'var(--surface-alt)' }}>
                    <input type="radio" className="sr-only" value={opt.val} checked={scopeMode === opt.val}
                      onChange={() => { setScopeMode(opt.val); setForm(f => ({ ...f, project_ids:[] })); }} />
                    <p className="font-medium text-sm" style={{ color: scopeMode === opt.val ? '#2E539F' : 'var(--text)' }}>
                      {opt.label}
                    </p>
                    <p className="text-xs mt-0.5" style={{ color:'var(--text-secondary)' }}>{opt.desc}</p>
                  </label>
                ))}
              </div>
            </div>

            {/* Project picker */}
            {scopeMode === 'projects' && (
              <div className="mb-4">
                <label className="label">Select Projects <span className="text-red-500">*</span></label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-1 max-h-44 overflow-y-auto p-2 rounded-xl border"
                  style={{ borderColor:'var(--border)' }}>
                  {projects.map(p => (
                    <label key={p.id}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${form.project_ids.includes(p.id) ? 'bg-brand-500/10' : 'hover:bg-[var(--surface-alt)]'}`}>
                      <input type="checkbox" checked={form.project_ids.includes(p.id)}
                        onChange={() => toggleProject(p.id)} />
                      <span className="text-sm truncate" style={{ color:'var(--text)' }}>{p.name}</span>
                    </label>
                  ))}
                </div>
                {form.project_ids.length > 0 && (
                  <p className="text-xs mt-1" style={{ color:'var(--text-secondary)' }}>
                    {form.project_ids.length} project{form.project_ids.length !== 1 ? 's' : ''} selected
                  </p>
                )}
              </div>
            )}

            <div className="mb-4">
              <label className="label">
                Reason
                <span className="ml-1 font-normal text-xs" style={{ color:'var(--text-secondary)' }}>(optional — helps your colleague understand context)</span>
              </label>
              <input className="input" value={form.reason}
                placeholder="e.g. Annual leave, Conference, Medical leave"
                onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} />
            </div>

            {error && <p className="text-sm text-red-500 mb-3">{error}</p>}

            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => { setShowForm(false); setError(''); }}
                className="btn-secondary text-sm">Cancel</button>
              <button type="submit" disabled={saving} className="btn-primary text-sm flex items-center gap-2">
                {saving && <span className="w-3.5 h-3.5 border border-white/30 border-t-white rounded-full animate-spin"/>}
                {saving ? 'Sending…' : 'Send Delegation Request'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── Tabs ──────────────────────────────────────────────── */}
      <div className="card p-0 overflow-hidden">
        <div className="flex border-b" style={{ borderColor:'var(--border)' }}>
          {[
            { key:'mine',     label:'My Delegations',       count: mine.length,     urgent: 0 },
            { key:'received', label:'Received Delegations', count: received.length, urgent: pendingCount },
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-6 py-3.5 text-sm font-medium border-b-2 transition-colors ${tab === t.key ? 'border-brand-500' : 'border-transparent hover:bg-[var(--surface-alt)]'}`}
              style={{ color: tab === t.key ? '#2E539F' : 'var(--text-secondary)' }}>
              {t.label}
              {t.count > 0 && (
                <span className="text-xs px-1.5 py-0.5 rounded-full font-bold"
                  style={{ background: tab === t.key ? '#2E539F' : 'var(--border)', color: tab === t.key ? '#fff' : 'var(--text-secondary)' }}>
                  {t.count}
                </span>
              )}
              {/* Urgent badge for pending items needing action */}
              {t.urgent > 0 && tab !== t.key && (
                <span className="text-xs px-1.5 py-0.5 rounded-full font-bold bg-amber-500 text-white">
                  {t.urgent} pending
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ── My Delegations table ──────────────────────────── */}
        {tab === 'mine' && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <Th>Delegate</Th>
                  <Th>Period</Th>
                  <Th>Days</Th>
                  <Th>Scope</Th>
                  <Th>Reason</Th>
                  <Th>Status</Th>
                  <Th>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="7" className="text-center py-10">
                    <div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin mx-auto"/>
                  </td></tr>
                ) : mine.length === 0 ? (
                  <EmptyRow cols={7} message="No delegations yet — click '+ New Delegation' to get started" />
                ) : mine.map(d => (
                  <tr key={d.id} className="hover:bg-[var(--surface-alt)] transition-colors">
                    <Td>
                      <div className="flex items-center gap-2">
                        <Av name={d.delegate_name} color={d.delegate_color} />
                        <span className="font-medium">{d.delegate_name}</span>
                      </div>
                    </Td>
                    <Td><span style={{ color:'var(--text-secondary)' }}>{fmtDate(d.start_date)} → {fmtDate(d.end_date)}</span></Td>
                    <Td><span style={{ color:'var(--text-secondary)' }}>{getDays(d)}d</span></Td>
                    <Td><ScopeCell d={d} /></Td>
                    <Td><span className="italic" style={{ color:'var(--text-secondary)' }}>{d.reason || '—'}</span></Td>
                    <Td><WorkflowBadge status={d.workflow_status} /></Td>
                    <Td>
                      <div className="flex gap-1.5 flex-wrap">
                        {/* Revoke available for pending and accepted delegations */}
                        {['pending_acceptance','accepted'].includes(d.workflow_status) && (
                          <button onClick={() => action(d.id, 'revoke', d.workflow_status === 'accepted' ? 'Revoke this delegation? Your colleague will be notified.' : 'Cancel this delegation request?')}
                            className="text-xs px-2.5 py-1.5 rounded-lg font-medium"
                            style={{ background:'#FEE2E2', color:'#991B1B' }}>
                            {d.workflow_status === 'accepted' ? 'Revoke' : 'Cancel'}
                          </button>
                        )}
                        {/* Delete only for ended/declined/revoked */}
                        {['declined','revoked'].includes(d.workflow_status) && (
                          <button onClick={() => handleDelete(d.id)}
                            className="text-xs px-2.5 py-1.5 rounded-lg btn-secondary">
                            Delete
                          </button>
                        )}
                        {d.workflow_status === 'accepted' && new Date(d.end_date + 'T00:00:00') < new Date() && (
                          <button onClick={() => handleDelete(d.id)}
                            className="text-xs px-2.5 py-1.5 rounded-lg btn-secondary">
                            Archive
                          </button>
                        )}
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ── Received Delegations table ────────────────────── */}
        {tab === 'received' && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <Th>Requested by</Th>
                  <Th>Period</Th>
                  <Th>Days</Th>
                  <Th>Scope</Th>
                  <Th>Reason</Th>
                  <Th>Status</Th>
                  <Th>Your Action</Th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="7" className="text-center py-10">
                    <div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin mx-auto"/>
                  </td></tr>
                ) : received.length === 0 ? (
                  <EmptyRow cols={7} message="No delegation requests received" />
                ) : received.map(d => (
                  <tr key={d.id}
                    className={`transition-colors hover:bg-[var(--surface-alt)] ${d.workflow_status === 'pending_acceptance' ? 'bg-amber-50/40 dark:bg-amber-900/10' : ''}`}>
                    <Td>
                      <div className="flex items-center gap-2">
                        <Av name={d.delegator_name} color={d.delegator_color} />
                        <span className="font-medium">{d.delegator_name}</span>
                      </div>
                    </Td>
                    <Td><span style={{ color:'var(--text-secondary)' }}>{fmtDate(d.start_date)} → {fmtDate(d.end_date)}</span></Td>
                    <Td><span style={{ color:'var(--text-secondary)' }}>{getDays(d)}d</span></Td>
                    <Td><ScopeCell d={d} /></Td>
                    <Td><span className="italic" style={{ color:'var(--text-secondary)' }}>{d.reason || '—'}</span></Td>
                    <Td><WorkflowBadge status={d.workflow_status} /></Td>
                    <Td>
                      {d.workflow_status === 'pending_acceptance' ? (
                        <div className="flex gap-1.5">
                          <button onClick={() => action(d.id, 'accept', null)}
                            className="text-xs px-2.5 py-1.5 rounded-lg font-medium"
                            style={{ background:'#D1FAE5', color:'#065f46' }}>
                            ✓ Accept
                          </button>
                          <button onClick={() => action(d.id, 'decline', 'Decline this delegation? The requester will be notified.')}
                            className="text-xs px-2.5 py-1.5 rounded-lg font-medium"
                            style={{ background:'#FEE2E2', color:'#991B1B' }}>
                            ✕ Decline
                          </button>
                        </div>
                      ) : d.workflow_status === 'accepted' ? (
                        <span className="text-xs text-emerald-600 font-medium">Covering ✓</span>
                      ) : (
                        <span className="text-xs" style={{ color:'var(--text-secondary)' }}>—</span>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── How it works ──────────────────────────────────────── */}
      <div className="mt-5 card-alt">
        <p className="font-semibold text-sm mb-3" style={{ color:'var(--text)' }}>Delegation workflow</p>
        <div className="flex flex-col md:flex-row gap-2 md:gap-0">
          {[
            { step:'Create',  desc:'You send a delegation request, specifying who, when, and which tasks or projects.' },
            { step:'Review',  desc:'Your colleague receives a notification and chooses to Accept or Decline.' },
            { step:'Active',  desc:'Once accepted, they see your delegated tasks on their task list and dashboard.' },
            { step:'End',     desc:'Delegation ends automatically on the due date, or you can Revoke it early.' },
          ].map((s, i) => (
            <div key={i} className="flex md:flex-col flex-row items-start md:items-center md:flex-1 gap-3 md:gap-1 md:text-center">
              <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                style={{ background:'#2E539F' }}>{i + 1}</div>
              <div>
                <p className="font-semibold text-sm" style={{ color:'var(--text)' }}>{s.step}</p>
                <p className="text-xs mt-0.5" style={{ color:'var(--text-secondary)' }}>{s.desc}</p>
              </div>
              {i < 3 && <div className="hidden md:block text-xl mx-2" style={{ color:'var(--border)' }}>→</div>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
